import React from 'react';
import Color from 'color';

import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button, Grid, Paper, styled, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import LanguageCurrencyField from '../../../components/inputs/LanguageCurrencyField';
import TextFieldBase from '../../../components/inputs/text-fields/base/TextFieldBase';
import { withFormController } from '../../../components/hocs/forms';

import { RemoveLargestJob } from '../../../console/agencies/quotes/PrimeX/requestQuote/supplementalFirmo/primeTech/components/RemoveLargestJob';
import { ErrorMessageText } from './common';

type JobListProps = { name: string; error?: string; disabled?: boolean };

export const JobList = (props: JobListProps) => {
  const formContext = useFormContext();
  const fieldArrayMethods = useFieldArray({
    control: formContext.control,
    name: `${props.name}.values`,
  });

  return (
    <React.Fragment>
      <Button
        startIcon={<AddIcon style={{ pointerEvents: 'none' }} />}
        className="contrast-text"
        onClick={() => fieldArrayMethods.append(BLANK_JOB)}
        variant="outlined"
        style={{
          marginTop: '0.15rem',
          padding: '0.25rem 1rem',
        }}
        disabled={props.disabled}
      >
        Add Job
      </Button>

      {fieldArrayMethods.fields.map((field, index) => {
        return (
          <Card elevation={2} key={field.id}>
            <Grid
              container
              columnSpacing={1}
              sx={{
                padding: '8px',
                position: 'relative',
                paddingRight: '24px',
              }}
            >
              <Grid item sm={3}>
                <ControlledTextField
                  name={`${props.name}.values.${index}.props.client.value`}
                  label="Project/Client Name"
                  disabled={props.disabled}
                />
              </Grid>

              <Grid item sm={2}>
                <ControlledTextField
                  type="date"
                  name={`${props.name}.values.${index}.props.date.value`}
                  label="Date services began"
                  disabled={props.disabled}
                />
              </Grid>

              <Grid item sm={3}>
                <ControlledTextField
                  name={`${props.name}.values.${index}.props.natureOfServices.value`}
                  label="Nature of Services Performed"
                  disabled={props.disabled}
                />
              </Grid>

              <Grid item sm={2}>
                <ControlledCurrencyField
                  name={`${props.name}.values.${index}.props.revenue.value`}
                  label="Revenue"
                  disabled={props.disabled}
                />
              </Grid>

              <Grid item sm={2}>
                <ControlledTextField
                  name={`${props.name}.values.${index}.props.percentGrossRevenue.value`}
                  label="% of Gross Revenue"
                  disabled={props.disabled}
                />
              </Grid>

              <ActionsContainer>
                <RemoveLargestJob
                  removeJob={() => fieldArrayMethods.remove(index)}
                />
              </ActionsContainer>
            </Grid>
          </Card>
        );
      })}

      <ErrorMessageText>{props.error}</ErrorMessageText>
    </React.Fragment>
  );
};

const Card = styled(Paper)(({ theme }) => {
  return {
    background:
      theme.palette.mode === 'dark'
        ? Color(theme.config.colors.darkGreyActive).darken(0.3).toString()
        : Color(theme.palette.background.active).lighten(0.065).toString(),
    borderRadius: 5,
    margin: '1rem 0',
    position: 'relative',
  };
});

const ActionsContainer = styled(Box)`
  position: absolute;
  right: 18px;
  top: 0px;
`;

const BLANK_JOB = {
  props: {
    client: {
      value: '',
    },
    date: {
      value: '',
    },
    natureOfServices: {
      value: '',
    },
    revenue: {
      value: '',
    },
    percentGrossRevenue: {
      value: '',
    },
  },
};

const ControlledTextField = withFormController(TextFieldBase);
const ControlledCurrencyField = withFormController(LanguageCurrencyField);
